import api from '@/services/api';
import session from '@/services/session';
import user from '@/services/user';
import {
  TokenId,
  TokenCode,
  TokenResponse,
} from './types';

const expirySessionKey = 't:e';
const tokenSessionKey = 't:v';

const hasTokenExpired = () => {
  const expiryTime = session.get(expirySessionKey) || false;
  return expiryTime && Date.now() > Number(expiryTime);
};

const expiredTokenRedirect = () => {
  hasTokenExpired()
    && clearToken()
    && (location.href = `/?r=${encodeURIComponent(location.pathname)}`);
};

const storeToken = (response: TokenResponse) => {
  const { expiry, token } = response;
  if (token && expiry) {
    // expiry in seconds so convert to ms
    session.set(expirySessionKey, expiry * 1000);
    session.set(tokenSessionKey, token);
    return user.storeInSession().then(data => ({
      ...response,
      user: data,
    }));
  }
  return response;
};

export type TokenPayload = TokenCode|TokenId;

export const attemptLogin = (email: string) => (
  api
    .post<TokenResponse>('/auth/attempt', { email })
    .then(storeToken)
);

export const clearToken = () => {
  session.clear(expirySessionKey);
  session.clear(tokenSessionKey);
  user.clearFromSession();
  return true;
};

export const getCurrentToken = () => session.get(tokenSessionKey);

export const getNewToken = (payload: TokenPayload) => (
  api
    .post<TokenResponse>('/auth/jwt', payload)
    .then(storeToken)
);

export const refreshTokenInterval = (minutes: number) => {
  // redirect immediately if token expired
  expiredTokenRedirect();

  const minsToMs = minutes * 60 * 1000;
  setInterval(() => {
    // refresh current token only if it exists
    const token = getCurrentToken();
    token && api
      .get<TokenResponse>('/auth/refresh', { token })
      .then(
        response => storeToken(response),
        err => {
          // TODO: display warning about possible auth issue
          err.code === 'AUTH_REFRESH_EXPIRED'
            && expiredTokenRedirect();
        },
      );
  }, minsToMs);
};

export default {
  attemptLogin,
  clearToken,
  getCurrentToken,
  getNewToken,
  refreshTokenInterval,
};
